import store from "@/store";

const RECONNECT_BASE_MS = 3_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * SSE transport for BackendSyncService using Mercure/EventSource.
 */
class BackendSyncSSEService {
	constructor({ topics = [] } = {}) {
		this.topics = topics;
		this.listeners = [];
		this.eventSource = null;
		this.reconnectTimer = null;
		this.reconnectAttempts = 0;
		this.intentionalDisconnect = false;

		this.handleOnline = this.handleOnline.bind(this);
		this.handleOffline = this.handleOffline.bind(this);
		window.addEventListener("online", this.handleOnline);
		window.addEventListener("offline", this.handleOffline);
	}

	handleOnline() {
		console.log("[BackendSyncSSEService] Device came online - reconnecting");
		this.reconnectAttempts = 0;
		this.reconnect();
	}

	handleOffline() {
		console.log("[BackendSyncSSEService] Device went offline");
		this.closeEventSource();
		this.emit("disconnected");
	}

	connect() {
		this.intentionalDisconnect = false;
		this.openEventSource();
	}

	disconnect() {
		this.intentionalDisconnect = true;
		this.clearReconnectTimer();
		this.closeEventSource();
		window.removeEventListener("online", this.handleOnline);
		window.removeEventListener("offline", this.handleOffline);
	}

	// eslint-disable-next-line no-unused-vars
	sendMessage(message) {
		// SSE is unidirectional — client-to-server goes through HTTP endpoints.
	}

	addMessageListener(listener) {
		this.listeners.push(listener);
	}

	removeMessageListener(listener) {
		this.listeners = this.listeners.filter((l) => l !== listener);
	}

	openEventSource() {
		this.closeEventSource();

		const { sseUrl } = store.state.config;

		if (!sseUrl || !this.topics.length) {
			console.warn("[BackendSyncSSEService] Missing SSE config — cannot connect", {
				sseUrl,
				topics: this.topics,
			});
			this.emit("error", "Missing SSE configuration");
			return;
		}

		this.emit("connecting");

		const url = new URL(sseUrl);
		this.topics.forEach((topic) => {
			url.searchParams.append("topic", topic);
		});

		try {
			this.eventSource = new EventSource(url.toString(), { withCredentials: true });
		} catch (e) {
			console.error("[BackendSyncSSEService] Failed to create EventSource:", e);
			this.emit("error", e.message);
			this.scheduleReconnect();
			return;
		}

		this.eventSource.onopen = () => {
			console.log("[BackendSyncSSEService] Connected to", this.topics);
			this.reconnectAttempts = 0;
			this.emit("connected");
		};

		this.eventSource.onmessage = (event) => {
			try {
				const envelope = JSON.parse(event.data);
				this.emit("message", envelope);
			} catch (e) {
				console.warn("[BackendSyncSSEService] Failed to parse event data:", e);
			}
		};

		this.eventSource.onerror = () => {
			console.warn("[BackendSyncSSEService] Connection error");
			this.closeEventSource();
			this.emit("disconnected");
			if (!this.intentionalDisconnect) {
				this.scheduleReconnect();
			}
		};
	}

	closeEventSource() {
		if (this.eventSource) {
			this.eventSource.close();
			this.eventSource = null;
		}
	}

	scheduleReconnect() {
		this.clearReconnectTimer();
		if (this.intentionalDisconnect) return;

		const delay = Math.min(
			RECONNECT_BASE_MS * Math.pow(1.5, this.reconnectAttempts),
			RECONNECT_MAX_MS,
		);
		this.reconnectAttempts++;
		console.log(
			`[BackendSyncSSEService] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`,
		);

		this.emit("connecting");
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			this.openEventSource();
		}, delay);
	}

	reconnect() {
		this.clearReconnectTimer();
		this.openEventSource();
	}

	clearReconnectTimer() {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	emit(type, data) {
		this.listeners.forEach((listener) => {
			try {
				listener(type, data);
			} catch (e) {
				console.error("[BackendSyncSSEService] Listener error:", e);
			}
		});
	}
}

export default BackendSyncSSEService;
